import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui/button';
import { Check, X, Loader2 } from 'lucide-react';

/**
 * QualityAgentNotification
 * Muestra una notificación de calidad para un campo (ej: descripción GBP).
 * Props:
 * - status: 'good' | 'bad' | 'recommended' (determina color y mensaje)
 * - message: string (mensaje a mostrar)
 * - onFix: función a ejecutar al hacer click en Fix
 * - onDismiss: función a ejecutar al hacer click en Dismiss
 * - suggestion: string (opcional, sugerencia para el fix)
 */
const QualityAgentNotification = ({
  status,
  message,
  onFix,
  onDismiss,
  suggestion,
  loadingFix,
  loadingDismiss,
  fixButtonText = 'Fix',
  isAnyOperationInProgress
}) => {
  const isGood = status === 'good';
  const isRecommended = status === 'recommended';
  const isBad = status === 'bad';

  // Determine styles based on status (similar to lovable.tsx)
  const styles = {
    good: 'bg-green-50 border-green-200 text-green-800',
    bad: 'bg-red-50 border-red-200 text-red-800',
    recommended: 'bg-blue-50 border-blue-200 text-blue-800'
  };

  const labels = {
    good: 'Good',
    bad: 'Critical',
    recommended: 'Suggestion'
  };

  return (
    <div className={`mt-3 p-4 rounded-xl border ${styles[status]}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex-1">
          <span className="text-xs font-semibold uppercase tracking-wide opacity-70">
            {labels[status]}:
          </span>{' '}
          <span className="text-sm">{message}</span>
          {(isBad || isRecommended) && suggestion && (
            <div className="text-sm opacity-80 mt-2">
              <strong>Suggestion:</strong> {suggestion}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {(isBad || isRecommended) && onFix && (
            <Button
              size="sm"
              onClick={onFix}
              disabled={
                loadingFix ||
                (isAnyOperationInProgress && isAnyOperationInProgress())
              }
              className="h-9 px-4 rounded-lg gap-2 font-semibold bg-green-600 hover:bg-green-700 text-white">
              {loadingFix ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {fixButtonText}
            </Button>
          )}
          {onDismiss && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDismiss}
              disabled={
                loadingDismiss ||
                (isAnyOperationInProgress && isAnyOperationInProgress())
              }
              className="h-9 px-4 rounded-lg gap-2 border-gray-300 hover:bg-gray-100">
              {loadingDismiss ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              Dismiss
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

QualityAgentNotification.propTypes = {
  status: PropTypes.oneOf(['good', 'bad', 'recommended']).isRequired,
  message: PropTypes.string.isRequired,
  onFix: PropTypes.func,
  onDismiss: PropTypes.func.isRequired,
  suggestion: PropTypes.string,
  loadingFix: PropTypes.bool,
  loadingDismiss: PropTypes.bool,
  fixButtonText: PropTypes.string,
  isAnyOperationInProgress: PropTypes.func
};

export default QualityAgentNotification;
